import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../services/api'
import './Course.css'
import { MainLayout } from '../../Layout/MainLayout'


interface ResponsibleArea {
    id: string
    name: string
}

export function CreateCourse() {
    const navigate = useNavigate()
    const [area, setArea] = useState<ResponsibleArea[]>([])
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const [error, setError] = useState('')

    const [cover, setCover] = useState<File | null>(null)
    const [coverPreview, setCoverPreview] = useState<string | null>(null)

    const [form, setForm] = useState({
        title: '',
        description: '',
        areaId: '',
        estimatedDurationMin: '',
        mandatory: '',
        order: '',
        status: ''
    })

    useEffect(() => {
        api.get('/responsible-area').then(r => setArea(r.data?.data ?? r.data)).catch(() => { })
    }, [])

    function set(field: string, value: string) {
        setForm(prev => ({ ...prev, [field]: value }))
        setError('')
    }

    function handleCoverChange(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0]
        if (!file) return

        const allowed = ['image/jpeg', 'image/png', 'image/webp']
        if (!allowed.includes(file.type)) {
            setError('Formato de imagem não suportado. Use JPG, PNG ou WEBP.')
            return
        }
        if (file.size > 5 * 1024 * 1024) {
            setError('Imagem muito grande. Máximo de 5MB.')
            return
        }

        setCover(file)
        setCoverPreview(URL.createObjectURL(file))
        setError('')
    }

    function validate() {
        if (!form.title.trim()) return 'Titulo é obrigatório'
        if (!form.description.trim()) return 'Descrição do Curso é obrigatório'
        if (!form.areaId) return 'Área do Cusro é obrigatória'
        return null
    }

    async function handleSubmit() {
        const err = validate()
        if (err) { setError(err); return }

        setLoading(true)
        setError('')
        try {
            const formData = new FormData()
            formData.append('title', form.title)
            formData.append('description', form.description)
            formData.append('estimatedDurationMin', form.estimatedDurationMin)
            formData.append('mandatory', form.mandatory)
            formData.append('areaId', form.areaId)

            if (cover) {
                formData.append('cover', cover)
            }

            await api.post('/course', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })

            setSuccess(true)
            setTimeout(() => navigate('/admin'), 1800)
        } catch (e: any) {
            setError(e.response?.data?.message || 'Erro ao criar Curso')
        } finally {
            setLoading(false)
        }
    }

    if (success) return (
        <MainLayout>
            <div className="cu-success-wrap">
                <div className="cu-success-icon">✓</div>
                <h2 className="serif">Curso criado!</h2>
                <p>Redirecionando para o painel...</p>
            </div>
        </MainLayout>
    )

    return (
        <MainLayout>
            <div className="container fade-in">
                <div className="breadcrumb">
                    <a onClick={() => navigate('/admin/')}>Admin</a>
                    <span className="sep">›</span>
                    <span>Novo Curso</span>
                </div>

                <div className="cu-layout">
                    <div className="cu-header">
                        <div className="kicker">Gestão de Cursos</div>
                        <h1 className="serif">Novo Curso</h1>
                        <p>Preencha as informações do Curso.</p>
                    </div>

                    <div className="cu-form-card">
                        {error && <div className="cu-error">{error}</div>}

                        <div className="cu-section-label">Capa do Curso</div>
                        <div className="cu-row">
                            <div className="field" style={{ flex: 1 }}>
                                <label>Imagem de Capa</label>
                                <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    onChange={handleCoverChange}
                                />
                            </div>
                            {coverPreview && (
                                <div className="field" style={{ flex: 1 }}>
                                    <img
                                        src={coverPreview}
                                        alt="Preview da capa"
                                        style={{ maxWidth: '160px', borderRadius: '8px' }}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="cu-section-label">Informaçõs do Curso</div>
                        <div className="cu-row">
                            <div className="field" style={{ flex: 1 }}>
                                <label>Titulo *</label>
                                <input
                                    type="text"
                                    value={form.title}
                                    onChange={e => set('title', e.target.value)}
                                    placeholder="Titulo do Curso"
                                />
                            </div>
                            <div className="field" style={{ flex: 2 }}>
                                <label>Descrição *</label>
                                <input
                                    type="text"
                                    value={form.description}
                                    onChange={e => set('description', e.target.value)}
                                    placeholder="Descrição do Curso"

                                />
                            </div>
                        </div>

                        <div className="cu-row">
                            <div className="field" style={{ flex: 1 }}>
                                <label>Tempo Estimado de Curso *</label>
                                <input
                                    type="text"
                                    value={form.estimatedDurationMin}
                                    onChange={e => set('estimatedDurationMin', e.target.value)}
                                    placeholder="Tempo de Curso"
                                />
                            </div>
                            <div className="field" style={{ flex: 1 }}>
                                <label>Obrigatório *</label>
                                <select
                                    value={form.mandatory}
                                    onChange={e => set('mandatory', e.target.value)}
                                    className="cu-select"
                                >
                                    <option value="false">Não</option>
                                    <option value="true">Sim</option>
                                </select>
                            </div>
                        </div>

                        <div className="field">
                            <label>Área *</label>
                            <select
                                value={form.areaId}
                                onChange={e => set('areaId', e.target.value)}
                                className="cu-select"
                            >
                                <option value="">Selecione a Área</option>
                                {area.map(a => (
                                    <option key={a.id} value={a.id}>
                                        {a.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="cu-actions">
                            <button
                                className="btn btn-ghost"
                                onClick={() => navigate('/admin')}
                                disabled={loading}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? 'Criando...' : 'Criar Courso →'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    )
}